/**
 * Logout / business-switch voice teardown (V4-LANGUAGE-STORAGE).
 */

import { getSharedTtsManager } from './ttsManager';
import {
  clearVoiceOperationalState,
  clearVoiceScope,
  type VoiceStorageScope,
} from './voiceStorage';

export type VoiceTeardownReason = 'logout' | 'business-switch';

/**
 * Cancel capture, stop TTS, clear pending/session operational state, and notify
 * the voice controller via CustomEvent. Call BEFORE flipping auth businessId
 * on switch, and BEFORE clearing the auth token on logout when possible.
 */
export function tearDownVoiceForScope(
  scope: VoiceStorageScope | null,
  reason: VoiceTeardownReason
): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent('voice-recording-cleanup', { detail: { reason } })
  );
  window.dispatchEvent(new CustomEvent('stopVoiceInput'));

  try {
    getSharedTtsManager().stop(reason);
  } catch {
    /* TTS manager may be unavailable in tests */
  }

  if (scope?.userId && scope?.businessId) {
    // On logout wipe the whole scope; on business switch clear operational
    // state for the outgoing business (settings/history stay namespaced).
    if (reason === 'logout') {
      clearVoiceScope(scope);
    } else {
      clearVoiceOperationalState(scope);
    }
  }

  // Always clear leftover flat legacy pending keys (cross-tab / pre-migration)
  try {
    localStorage.removeItem('voicePendingCommandId');
    localStorage.removeItem('voicePendingContactConfirmation');
    localStorage.removeItem('voicePendingContactDisambiguation');
    localStorage.removeItem('voicePendingDocumentFlow');
    localStorage.removeItem('voiceSessionId');
  } catch {
    /* ignore */
  }

  window.dispatchEvent(
    new CustomEvent(reason === 'logout' ? 'voiceLogout' : 'voiceBusinessSwitch', {
      detail: { reason, scope },
    })
  );
}
